// ── News Monitor ─────────────────────────────────────────────────────────────
//
// Negative news on the people and companies we lend to, without anyone setting
// up a Google Alert. The watch list is built from ACE's own tables — every
// funded account's company name, its borrowers and guarantors, and any contact
// with "Monitor news" ticked — and each name is searched weekly (Mondays) on Google
// News through Serper.dev. Every story that names them is filed; its trouble
// words make it High or Medium, none makes it Low. Every new story is a
// finding to work, like MCA Monitor.
//
//   Stories        what the search found, grouped by borrower (collapsible).
//                  Acknowledge (it's us, we know) or
//                  Not them (same name, someone else — note required).
//   Watched names  who is searched, how, and when it last ran. Exclude a name,
//                  search it under other words ("Search as"). Contacts are
//                  watched from the contact itself (Monitor news).
//
// The search itself: api/runNewsScan, weekly from the scheduler, or Run Scan
// here. One call searches ~25 names (30 seconds); Run Scan keeps calling until
// every name due is done.
import { useState, useEffect, useRef, Fragment } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, Field, Modal, SearchBar, SectionHeader, Spinner, Td, STh } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { LastRunTile } from "../components/LastRunTile";

const STATUSES = ["Open", "Acknowledged", "Not them", "all"];
const SEV = {
  High:   { bg: "#FBE4DD", fg: "#993C1D" },
  Medium: { bg: "#FDF0D5", fg: "#7A5200" },
  Low:    { bg: "#F1EFE8", fg: "#5F5E5A" },   // names them, no trouble word
};
const STATUS_TONE = {
  "Open":         { bg: "#E6F7FC", fg: B.cyanDark },
  "Acknowledged": { bg: "#FDF0D5", fg: "#7A5200" },
  "Not them":     { bg: "#F1EFE8", fg: "#5F5E5A" },
};
const GREY = { bg: "#F1EFE8", fg: "#5F5E5A" };
const Pill = ({ tone, children, title }) => (
  <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                               background: tone.bg, color: tone.fg }}>{children}</span>
);
const Tile = ({ label, value, tone, onClick, active, sub }) => (
  <div onClick={onClick}
       style={{ background: active ? "#E6F7FC" : "var(--color-background-secondary)",
                border: "0.5px solid " + (active ? B.cyan : "var(--color-border-tertiary)"),
                borderRadius: 8, padding: "10px 14px", cursor: onClick ? "pointer" : "default", minWidth: 130 }}>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: tone || "inherit" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{sub}</div>}
  </div>
);
const Chip = ({ on, onClick, children }) => (
  <span onClick={onClick}
        style={{ cursor: "pointer", fontSize: 12, padding: "4px 12px", borderRadius: 14, fontWeight: on ? 700 : 400,
                 background: on ? B.cyan : "transparent", color: on ? "#fff" : B.cyanDark,
                 border: "1px solid " + (on ? B.cyan : B.cyanLight) }}>{children}</span>
);
const when = d => d ? new Date(d).toLocaleString() : "—";
const inputCss = { width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13, borderRadius: 4,
                   border: "1px solid #999", fontFamily: "inherit" };

export const NewsMonitorPage = () => {
  const { api, go, role } = useApp();
  const [tab, setTab] = useState("stories");
  const [status, setStatus] = useState("Open");
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [decide, setDecide] = useState(null);      // { ids, status }
  const [edit, setEdit] = useState(null);          // subject being edited
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);
  const stopRef = useRef(false);
  const canEdit = !!role.canEdit;

  const load = (s = status) => {
    setErr(null);
    api.get("getNewsFindings", { status: s }).then(d => { setData(d); setSel({}); }).catch(e => setErr(e.message));
  };
  const loadSubs = () => api.get("getNewsSubjects").then(setSubs).catch(e => setErr(e.message));
  useEffect(() => { load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "names" && !subs) loadSubs(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Run Scan: every name, now — "not searched since I pressed the button" —
  // calling until none is left, or Stop. Each call is ~25 names.
  const runScan = async (subjectkey) => {
    setRunning(true); stopRef.current = false;
    const since = new Date().toISOString();
    let names = 0, stories = 0, errors = 0;
    try {
      for (let i = 0; i < 60 && !stopRef.current; i++) {
        const r = await api.post("runNewsScan", subjectkey ? { subjectkey } : { since });
        names += r.scanned || 0; stories += r.newfindings || 0; errors += r.errors || 0;
        setRunMsg(`Searched ${names} name${names === 1 ? "" : "s"} · ${stories} new stor${stories === 1 ? "y" : "ies"}`
                + (errors ? ` · ${errors} error${errors === 1 ? "" : "s"}` : "")
                + (r.remaining ? ` · ${r.remaining} still to search…` : " · done"));
        if (r.stopped) { setRunMsg(m => (m || "") + " · stopped: " + r.stopped); break; }
        if (subjectkey || !r.remaining) break;
        if (r.rateLimited) await new Promise(res => setTimeout(res, 8000));
      }
      if (stopRef.current) setRunMsg(m => (m || "") + " · stopped");
    } catch (e) { setRunMsg("Scan failed: " + e.message); }
    setRunning(false);
    load(); if (subs || tab === "names") loadSubs();
  };

  if (err) return <ErrorMsg msg={err} onRetry={() => { load(); if (tab === "names") loadSubs(); }} />;
  if (!data) return <Spinner />;

  const cnt = (s, v) => (data.counts || []).filter(x => x.status === s && (!v || x.severity === v)).reduce((t, x) => t + x.n, 0);
  const lr = data.lastRun, d24 = data.last24h || {};

  return <>
    <SectionHeader title="News Monitor" actions={[
      tab === "stories" ? <SearchBar key="q" value={q} onChange={setQ} placeholder="Borrower, name or headline…" /> : null,
      running ? <Btn key="stop" onClick={() => { stopRef.current = true; }}>Stop</Btn> : null,
      // Admins only: every search spends a Serper credit; the 4:10 am run covers everyone.
      !role.isAdmin ? null : <Btn key="run" variant="primary" disabled={running} onClick={() => runScan()}
           title="Search every watched name now — one Serper credit each">{running ? "Searching…" : "Run Scan"}</Btn>,
    ]} />

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
      <Tile label="Open · High" value={cnt("Open", "High")} tone={cnt("Open", "High") ? "#993C1D" : undefined}
            active={tab === "stories" && status === "Open"} onClick={() => { setTab("stories"); setStatus("Open"); }}
            sub="bankruptcy, fraud, charges…" />
      <Tile label="Open · Medium" value={cnt("Open", "Medium")} tone={cnt("Open", "Medium") ? "#7A5200" : undefined}
            active={tab === "stories" && status === "Open"} onClick={() => { setTab("stories"); setStatus("Open"); }}
            sub="lawsuits, liens, layoffs…" />
      <Tile label="Open · Low" value={cnt("Open", "Low")}
            active={tab === "stories" && status === "Open"} onClick={() => { setTab("stories"); setStatus("Open"); }}
            sub="no trouble word" />
      <Tile label="Acknowledged" value={cnt("Acknowledged")} active={tab === "stories" && status === "Acknowledged"}
            onClick={() => { setTab("stories"); setStatus("Acknowledged"); }} />
      <Tile label="Not them" value={cnt("Not them")} active={tab === "stories" && status === "Not them"}
            onClick={() => { setTab("stories"); setStatus("Not them"); }} />
      <Tile label="Last 24 hours" value={`${d24.scanned || 0} names`}
            sub={`${d24.newfindings || 0} new · ${d24.errors || 0} error${d24.errors === 1 ? "" : "s"}`}
            tone={d24.errors ? "#993C1D" : undefined} onClick={() => setTab("names")} active={tab === "names"} />
      {/* Same "is it still running" tile as UCC and Tax: red after 8 days (weekly
          Monday run, plus a day) or when the last run searched nothing and failed. */}
      <LastRunTile label="Last Scan" staleHours={192}
                   run={{ at: lr ? (lr.finishedat || lr.startedat) : null, by: lr && lr.ranby,
                          error: lr && lr.errors && !lr.scanned ? (lr.errormessage || "failed") : null,
                          note: lr ? `${lr.scanned || 0} names · ${lr.remaining || 0} left` : null }} />
    </div>

    <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
      <Chip on={tab === "stories"} onClick={() => setTab("stories")}>Stories</Chip>
      <Chip on={tab === "names"} onClick={() => setTab("names")}>Watched names</Chip>
      {runMsg && <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 10 }}>{runMsg}</span>}
    </div>

    {tab === "stories"
      ? <Stories data={data} status={status} setStatus={setStatus} q={q} sel={sel} setSel={setSel}
                 canEdit={canEdit} onDecide={setDecide} go={go} />
      : <Names subs={subs} canEdit={canEdit} isAdmin={!!role.isAdmin} running={running}
               onEdit={setEdit} onSearch={k => runScan(k)} reload={loadSubs} />}

    {decide && <DecisionModal decide={decide} onClose={() => setDecide(null)}
                              onSaved={() => { setDecide(null); load(); }} />}
    {edit && <SubjectModal s={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); loadSubs(); }} />}
  </>;
};

// ── Stories ─────────────────────────────────────────────────────────────────
// Grouped by borrower: one header row per account (worst severity first),
// its stories under it. A borrower with a High or Medium story opens expanded;
// one with only Low stories starts collapsed. Click a header to open or close
// it; Expand all / Collapse all do the lot. Column sorts apply inside each group.
const SEV_RANK = { High: 3, Medium: 2, Low: 1 };
const Stories = ({ data, status, setStatus, q, sel, setSel, canEdit, onDecide, go }) => {
  const [openMap, setOpenMap] = useState({});   // accountid -> true/false, set by the user
  const rows = (data.findings || [])
    .filter(f => !q || [f.companyname, f.subjectname, f.title, f.domain, f.source, f.snippet].some(x => String(x || "").toLowerCase().includes(q.toLowerCase())))
    .map(f => ({ ...f, sevrank: SEV_RANK[f.severity] || 1, seen: f.seendate || f.createdat }));
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, "", "desc");
  const H = ({ label, k }) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} />;
  const selIds = Object.keys(sel).filter(k => sel[k]).map(Number);
  const allSel = sorted.length > 0 && sorted.every(f => sel[f.id]);

  // Groups, in the order: worst severity, then most High, then most recent story.
  const byAcct = new Map();
  for (const f of sorted) {
    if (!byAcct.has(f.accountid)) byAcct.set(f.accountid, { accountid: f.accountid, companyname: f.companyname, items: [] });
    byAcct.get(f.accountid).items.push(f);
  }
  const groups = [...byAcct.values()].map(g => {
    const n = sev => g.items.filter(f => f.severity === sev).length;
    const latest = g.items.map(f => f.seen).filter(Boolean).sort().pop();
    return { ...g, high: n("High"), medium: n("Medium"), low: n("Low"), latest,
             worst: Math.max(...g.items.map(f => f.sevrank)) };
  }).sort((a, b) => b.worst - a.worst || b.high - a.high || b.medium - a.medium
                 || String(b.latest || "").localeCompare(String(a.latest || "")));
  const isOpen = g => g.accountid in openMap ? openMap[g.accountid] : g.worst >= 2;
  const setAll = v => setOpenMap(Object.fromEntries(groups.map(g => [g.accountid, v])));
  const flip = g => setOpenMap(m => ({ ...m, [g.accountid]: !isOpen(g) }));
  const COLS = 7;

  return <>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
      {STATUSES.map(s => <Chip key={s} on={status === s} onClick={() => setStatus(s)}>{s === "all" ? "All" : s}</Chip>)}
      {groups.length > 1 && <span style={{ marginLeft: 8, display: "inline-flex", gap: 6 }}>
        <Btn onClick={() => setAll(true)}>Expand all</Btn>
        <Btn onClick={() => setAll(false)}>Collapse all</Btn>
      </span>}
      <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {groups.length} borrower{groups.length === 1 ? "" : "s"} · {sorted.length} stor{sorted.length === 1 ? "y" : "ies"}</span>
        {canEdit && selIds.length > 0 && <>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>· {selIds.length} selected</span>
          <Btn onClick={() => onDecide({ ids: selIds, status: "Acknowledged" })}>Acknowledge</Btn>
          <Btn onClick={() => onDecide({ ids: selIds, status: "Not them" })}>Not them</Btn>
          {status !== "Open" && <Btn onClick={() => onDecide({ ids: selIds, status: "Open" })}>Reopen</Btn>}
        </>}
        <Btn onClick={() => exportCSV(sorted, ["companyname", "subjectkind", "subjectname", "title", "snippet", "url", "source", "domain", "seendate",
            "severity", "matchedterms", "alsoseen", "status", "decidedby", "decidedat", "note"], "news_findings.csv")}>Export</Btn>
      </span>
    </div>

    <Card style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={{ width: 30, padding: "8px 10px", background: "var(--color-background-secondary)" }}>
            {canEdit && <input type="checkbox" checked={allSel}
                   onChange={e => setSel(e.target.checked ? Object.fromEntries(sorted.map(f => [f.id, true])) : {})} />}
          </th>
          <H label="Name searched" k="subjectname" />
          <H label="Story" k="title" />
          <H label="Seen" k="seen" />
          <H label="Severity" k="sevrank" />
          <H label="Status" k="status" />
          <th style={{ background: "var(--color-background-secondary)" }} />
        </tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={COLS} style={{ padding: 24, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
            {status === "Open" ? "Nothing open. Every story found has been reviewed." : "No stories with this status."}
          </td></tr>}
          {groups.map(g => {
            const open = isOpen(g);
            const gSel = g.items.every(f => sel[f.id]);
            return <Fragment key={g.accountid}>
              {/* Borrower header: click anywhere but the checkbox and the link to open / close. */}
              <tr onClick={() => flip(g)} style={{ cursor: "pointer", background: "var(--color-background-secondary)",
                                                   borderTop: "1px solid var(--color-border-tertiary)" }}>
                <td style={{ padding: "8px 10px", width: 30 }} onClick={e => e.stopPropagation()}>
                  {canEdit && <input type="checkbox" checked={gSel} title="Select this borrower's stories"
                    onChange={e => setSel(s => ({ ...s, ...Object.fromEntries(g.items.map(f => [f.id, e.target.checked])) }))} />}
                </td>
                <td colSpan={COLS - 1} style={{ padding: "8px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ display: "inline-block", width: 12, color: "var(--color-text-secondary)", fontSize: 11,
                                   transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▶</span>
                    <a href="#" onClick={e => { e.preventDefault(); e.stopPropagation(); go("account", { accountid: g.accountid, companyname: g.companyname }); }}
                       style={{ color: B.cyanDark, textDecoration: "none", fontWeight: 600, fontSize: 13.5 }}>{g.companyname || g.accountid}</a>
                    {g.high > 0 && <Pill tone={SEV.High}>{g.high} High</Pill>}
                    {g.medium > 0 && <Pill tone={SEV.Medium}>{g.medium} Medium</Pill>}
                    {g.low > 0 && <Pill tone={SEV.Low}>{g.low} Low</Pill>}
                    <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--color-text-secondary)" }}>
                      {g.items.length} stor{g.items.length === 1 ? "y" : "ies"}{g.latest ? ` · latest ${fmtDate(g.latest)}` : ""}</span>
                  </div>
                </td>
              </tr>
              {open && g.items.map(f => (
                <tr key={f.id} style={{ verticalAlign: "top" }}>
                  <Td style={{ width: 30 }}>
                    {canEdit && <input type="checkbox" checked={!!sel[f.id]} onChange={e => setSel(s => ({ ...s, [f.id]: e.target.checked }))} />}
                  </Td>
                  <Td style={{ paddingLeft: 30 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                                  color: "var(--color-text-secondary)" }}>{f.subjectkind}</div>
                    <div style={{ fontSize: 13 }}>{f.subjectname}</div>
                  </Td>
                  <Td style={{ minWidth: 320 }}>
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                       style={{ color: "var(--color-text-primary)", fontWeight: 500, textDecoration: "none" }}>{f.title} ↗</a>
                    <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 2 }}>
                      {f.source || f.domain}{f.alsoseen > 0 ? ` · also on ${f.alsoseen} other site${f.alsoseen === 1 ? "" : "s"}` : ""}
                      {f.matchedterms ? ` · ${f.matchedterms}` : ""}
                    </div>
                    {f.snippet && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 3 }}>{f.snippet}</div>}
                    {f.note && <div style={{ fontSize: 12, marginTop: 4, whiteSpace: "pre-wrap" }}>
                      <b>{f.status}</b> by {f.decidedby} · {when(f.decidedat)}: {f.note}</div>}
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(f.seen)}</Td>
                  <Td><Pill tone={SEV[f.severity] || SEV.Medium}>{f.severity}</Pill></Td>
                  <Td><Pill tone={STATUS_TONE[f.status] || STATUS_TONE.Open}>{f.status}</Pill></Td>
                  <Td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    {canEdit && f.status === "Open" && <span style={{ display: "inline-flex", gap: 6 }}>
                      <Btn onClick={() => onDecide({ ids: [f.id], status: "Acknowledged" })}>Acknowledge</Btn>
                      <Btn onClick={() => onDecide({ ids: [f.id], status: "Not them" })}>Not them</Btn>
                    </span>}
                    {canEdit && f.status !== "Open" && <Btn onClick={() => onDecide({ ids: [f.id], status: "Open" })}>Reopen</Btn>}
                  </Td>
                </tr>
              ))}
            </Fragment>;
          })}
        </tbody>
      </table>
    </Card>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8 }}>
      Funded borrowers (Accrual / Non Accrual). Every story with the name in its headline or snippet is filed: High or Medium by
      its worst trouble word, Low when it has none (Low doesn't send the email or count on the badge). A word match is not proof — open the story before deciding.
    </div>
  </>;
};

// ── Watched names ───────────────────────────────────────────────────────────
// Grouped by borrower, like Stories: one header row per account, its names
// under it. Groups start closed, except a borrower with a name that needs
// attention (can't be searched as it stands, or its last search failed).
const Names = ({ subs, canEdit, isAdmin, running, onEdit, onSearch, reload }) => {
  const { go } = useApp();
  const [openMap, setOpenMap] = useState({});   // accountid -> true/false, set by the user
  const rows = (subs && subs.subjects) || [];
  const allKw = ((subs && subs.keywords) || []).filter(k => k.isactive);
  const activeKw = allKw.filter(k => k.severity !== "Ignore");
  const ignoreKw = allKw.filter(k => k.severity === "Ignore");
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, "kind", "asc");
  const H = ({ label, k }) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} />;
  if (!subs) return <Spinner />;

  const searched = rows.filter(s => !s.exclude).length;
  const notSearchable = rows.filter(s => !s.exclude && s.unsearchable).length;

  // Groups, alphabetical by borrower; names inside follow the column sort.
  const byAcct = new Map();
  for (const s of sorted) {
    if (!byAcct.has(s.accountid)) byAcct.set(s.accountid, { accountid: s.accountid, companyname: s.companyname, items: [] });
    byAcct.get(s.accountid).items.push(s);
  }
  const dupes = subs.duplicates || [];
  const groups = [...byAcct.values()].map(g => ({
    ...g,
    cant:    g.items.filter(s => !s.exclude && s.unsearchable).length,
    errors:  g.items.filter(s => !s.exclude && s.laststatus === "error").length,
    off:     g.items.filter(s => s.exclude).length,
    stories: g.items.reduce((t, s) => t + (s.openfindings || 0), 0),
    last:    g.items.map(s => s.lastscanat).filter(Boolean).sort().pop(),
    folded:  dupes.filter(d => d.accountid === g.accountid),
  })).sort((a, b) => String(a.companyname || "").localeCompare(String(b.companyname || "")));
  const isOpen = g => g.accountid in openMap ? openMap[g.accountid] : (g.cant + g.errors) > 0;
  const setAll = v => setOpenMap(Object.fromEntries(groups.map(g => [g.accountid, v])));
  const flip = g => setOpenMap(m => ({ ...m, [g.accountid]: !isOpen(g) }));
  const COLS = 5;

  return <>
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12,
                  color: "var(--color-text-secondary)", marginBottom: 10 }}>
      {groups.length > 1 && <span style={{ display: "inline-flex", gap: 6 }}>
        <Btn onClick={() => setAll(true)}>Expand all</Btn>
        <Btn onClick={() => setAll(false)}>Collapse all</Btn>
      </span>}
      <span>To watch a contact, open it and tick <b>Monitor news</b>.</span>
      <span style={{ marginLeft: "auto" }}>
        {groups.length} borrower{groups.length === 1 ? "" : "s"} · {searched} names searched weekly{notSearchable ? ` · ${notSearchable} can't be searched as they stand` : ""}
        {dupes.length ? ` · ${dupes.length} same as another name on the account` : ""}
      </span>
    </div>

    <Card style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <H label="Name" k="kind" />
          <H label="Searched for" k="query" />
          <H label="Last searched" k="lastscanat" />
          <H label="Open" k="openfindings" />
          <th style={{ background: "var(--color-background-secondary)" }} />
        </tr></thead>
        <tbody>{groups.map(g => {
          const open = isOpen(g);
          return <Fragment key={g.accountid}>
            {/* Borrower header: click to open / close; the name opens the account. */}
            <tr onClick={() => flip(g)} style={{ cursor: "pointer", background: "var(--color-background-secondary)",
                                                 borderTop: "1px solid var(--color-border-tertiary)" }}>
              <td colSpan={COLS} style={{ padding: "8px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-block", width: 12, color: "var(--color-text-secondary)", fontSize: 11,
                                 transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▶</span>
                  <a href="#" onClick={e => { e.preventDefault(); e.stopPropagation(); go("account", { accountid: g.accountid, companyname: g.companyname }); }}
                     style={{ color: B.cyanDark, textDecoration: "none", fontWeight: 600, fontSize: 13.5 }}>{g.companyname || g.accountid}</a>
                  <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                    {g.items.length} name{g.items.length === 1 ? "" : "s"}{g.folded.length ? ` (+${g.folded.length} same)` : ""}</span>
                  {g.errors > 0 && <Pill tone={SEV.High}>{g.errors} error{g.errors === 1 ? "" : "s"}</Pill>}
                  {g.cant > 0 && <Pill tone={SEV.Medium}>{g.cant} can't be searched</Pill>}
                  {g.off > 0 && <Pill tone={GREY}>{g.off} not searched</Pill>}
                  {g.stories > 0 && <Pill tone={STATUS_TONE.Open}>{g.stories} open stor{g.stories === 1 ? "y" : "ies"}</Pill>}
                  <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--color-text-secondary)" }}>
                    {g.last ? `last searched ${fmtDate(g.last)}` : "not searched yet"}</span>
                </div>
              </td>
            </tr>
            {open && g.items.map(s => (
              <tr key={s.subjectkey} style={{ verticalAlign: "top", opacity: s.exclude ? 0.55 : 1 }}>
                <Td style={{ paddingLeft: 30 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                                color: "var(--color-text-secondary)" }}>{s.kind}{s.isperson ? " · person" : ""}</div>
                  <div>{s.name}</div>
                </Td>
                <Td style={{ maxWidth: 380 }}>
                  {s.exclude ? <><Pill tone={GREY}>not searched</Pill>
                      {s.settingnote && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 3 }}>{s.settingnote}</div>}</>
                    : s.unsearchable ? <span style={{ fontSize: 12, color: "#7A5200" }}>{s.unsearchable}</span>
                    : <span style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)", wordBreak: "break-word" }}
                            title={s.query}>{s.query || ""}{s.searchas ? <span style={{ fontFamily: "inherit", marginLeft: 6 }}><Pill tone={STATUS_TONE.Open}>search as</Pill></span> : null}</span>}
                </Td>
                <Td style={{ whiteSpace: "nowrap" }}>
                  {s.lastscanat ? fmtDate(s.lastscanat) : <span style={{ color: "var(--color-text-secondary)" }}>not yet</span>}
                  {s.laststatus === "error" && <div style={{ fontSize: 11.5, color: "#993C1D", whiteSpace: "normal", maxWidth: 260 }} title={s.lasterror}>
                    {String(s.lasterror || "").slice(0, 90)}</div>}
                  {s.laststatus === "ok" && <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>
                    {s.lastfound || 0} article{s.lastfound === 1 ? "" : "s"} · {s.lastnew || 0} new</div>}
                </Td>
                <Td style={{ fontWeight: s.openfindings ? 600 : 400, color: s.openfindings ? "#7A5200" : undefined }}>{s.openfindings || "—"}</Td>
                <Td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  {canEdit && <span style={{ display: "inline-flex", gap: 6 }}>
                    {isAdmin && !s.exclude && !s.unsearchable && <Btn disabled={running} onClick={() => onSearch(s.subjectkey)}>Search now</Btn>}
                    <Btn onClick={() => onEdit(s)}>Settings</Btn>
                  </span>}
                </Td>
              </tr>
            ))}
            {open && g.folded.length > 0 && <tr><td colSpan={COLS} style={{ padding: "4px 10px 8px 30px", fontSize: 12, color: "var(--color-text-secondary)" }}>
              Same as a name above, searched once: {g.folded.map(d => d.name).join(", ")}</td></tr>}
          </Fragment>;
        })}</tbody>
      </table>
    </Card>

    {/* The trouble words: shown here, edited on Integrations → News (Serper)
        next to the other search settings, since they decide what is filed. */}
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 10 }}>
      Trouble words: {activeKw.length} active ({activeKw.filter(k => k.severity === "High").length} High){ignoreKw.length ? ` · ${ignoreKw.length} ignore word${ignoreKw.length === 1 ? "" : "s"}` : ""}.
      They set a story's severity (none = Low).{" "}
      {isAdmin && <a href="#" onClick={e => { e.preventDefault(); go("integration-news"); }}
                     style={{ color: B.cyanDark }}>Edit them under Integrations → News (Serper)</a>}
    </div>
  </>;
};

// ── Settings for one name ───────────────────────────────────────────────────
// Exported: the account's Monitoring tab opens the same windows.
export const SubjectModal = ({ s, onClose, onSaved }) => {
  const { api } = useApp();
  const [searchas, setSearchas] = useState(s.searchas || "");
  const [exclude, setExclude] = useState(!!s.exclude);
  const [note, setNote] = useState(s.settingnote || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const isContact = s.kind === "Contact";
  const save = async (body) => {
    setSaving(true); setErr(null);
    try { await api.post("saveNewsSubject", { subjectkey: s.subjectkey, ...body }); onSaved(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };
  return (
    <Modal title={`${s.kind}: ${s.name}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12 }}>{s.companyname}</div>
      <Field label="Search as">
        <input value={searchas} onChange={e => setSearchas(e.target.value)} style={inputCss}
               placeholder={`Blank = the name itself, without LLC / Inc`} />
      </Field>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", margin: "-4px 0 14px" }}>
        Use for a name too short or too common to search alone ("Jeg and Sons Trucking"), or a brand the company trades
        under. Searched as typed (no quotes needed — they're removed), and a story must contain these words in order; for a person, the state is no longer added.
      </div>
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginBottom: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={exclude} onChange={e => setExclude(e.target.checked)} style={{ marginTop: 2 }} />
        <span>Don't search this name.</span>
      </label>
      <Field label={exclude ? "Why not (required)" : "Note"}>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={inputCss}
                  placeholder={exclude ? "e.g. Name shared with a national chain; every story is about them" : ""} />
      </Field>
      {isContact && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        To stop watching this person, untick <b>Monitor news</b> on the contact.</div>}
      {s.lastquery && <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)",
                                     wordBreak: "break-word", marginBottom: 10 }}>Last search: {s.lastquery}</div>}
      {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving || (exclude && !note.trim())}
             onClick={() => save({ searchas, exclude, note })}>{saving ? "Saving…" : "Save"}</Btn>
      </div>
    </Modal>
  );
};

// ── Acknowledge / Not them / Reopen ─────────────────────────────────────────
// Exported: the account's Monitoring tab opens the same windows.
export const DecisionModal = ({ decide, onClose, onSaved }) => {
  const { api } = useApp();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const needsNote = decide.status === "Not them";
  const help = {
    "Acknowledged": "It's about our borrower and we know. The note goes on the account's Notes tab.",
    "Not them":     "A different company or person with the same name. Say how you told them apart. If this name keeps matching strangers, set Search as on Watched names.",
    "Open":         "Put it back on the list.",
  }[decide.status];
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api.post("saveNewsFinding", { ids: decide.ids, status: decide.status, note });
      if (r && r.errors && r.errors.length) { setErr(r.errors.map(e => e.error).join("; ")); setSaving(false); return; }
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };
  return (
    <Modal title={`${decide.status === "Open" ? "Reopen" : decide.status} · ${decide.ids.length} stor${decide.ids.length > 1 ? "ies" : "y"}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 14 }}>{help}</div>
      <Field label="Note" required={needsNote}>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={4} style={inputCss}
                  placeholder={needsNote ? "e.g. Story is about a Hobbs Fibers in Ohio; ours is in Texas" : "What you found"} />
      </Field>
      {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving || (needsNote && !note.trim())} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
      </div>
    </Modal>
  );
};
